import type { Message, ToolCall } from '@/context/agent-state.context';

/** The reply's text written before the turn's first tool call. */
export function toolsLead(toolCalls: readonly ToolCall[]): string {
  return toolCalls[0]?.textBefore ?? '';
}

export interface TurnLayout {
  /** The thread up to and including the user message that started the turn. */
  before: Message[];
  /** Reply text written before the turn's first tool call: above the tools. */
  lead: string;
  /** The turn's messages after the tools. */
  after: Message[];
  /** What the first of `after` shows below the tools: its reply minus the
   * lead, when the lead is drawn above them. */
  replyShown?: string;
  /** Streamed reply text written after the tools. */
  tail: string;
}

/**
 * Splits the current turn around its tool block, so the order on screen is
 * the order it happened in: what the agent said first, the tools, the rest.
 * The tool block is anchored to the user message that started the turn;
 * otherwise it gets pushed below the reply and reads as if it came after.
 */
export function layoutTurn({
  messages,
  toolCalls,
  streamingText,
  streaming,
}: {
  messages: readonly Message[];
  toolCalls: readonly ToolCall[];
  /** The reply streamed so far, as shown. */
  streamingText: string;
  streaming: boolean;
}): TurnLayout {
  const lead = toolsLead(toolCalls);
  const lastUserIdx = messages.findLastIndex((m) => m.role === 'user');
  const before = messages.slice(0, lastUserIdx + 1);
  const after = messages.slice(lastUserIdx + 1);
  const tail = streaming ? streamingText.slice(lead.length) : '';
  const reply = after[0];
  // Once the reply is a message, its lead still sits above the tools; only
  // the reply those tools belong to, not a later voice-only answer.
  if (lead && reply?.role === 'assistant' && reply.content.startsWith(lead)) {
    return {
      before,
      lead,
      after,
      replyShown: reply.content.slice(lead.length),
      tail,
    };
  }
  return {
    before,
    lead: streaming ? streamingText.slice(0, lead.length) : '',
    after,
    tail,
  };
}
